import { FormDrawer, FormDrawerContent, Id, TextareaField, isSuccess, useToasts } from "@dgs/core";
import { useQueryClient } from "@tanstack/react-query";
import { FC, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { eventUserRequestService } from "../../../shared/api/eventUserRequestService";
import { DeclineFormValues } from "../../../shared/types/eventUserRequest";

interface Props {
	editEntityId: Id;
	open: boolean;
	onClose: () => void;
	onClosed: () => void;
}

const emptyDeclineFormValues: DeclineFormValues = { declinationReason: "" };

export const DeclineEventUserRequestDrawer: FC<Props> = ({ editEntityId, ...drawerProps }) => {
	const { t } = useTranslation();
	const queryClient = useQueryClient();
	const { showToast } = useToasts();

	const decline = useCallback(
		async (declineFormValues: DeclineFormValues) => {
			const result = await eventUserRequestService.declineRequest(editEntityId, declineFormValues);
			if (isSuccess(result)) {
				await queryClient.invalidateQueries({ queryKey: eventUserRequestService.openRequestsKey });
				showToast({
					type: "success",
					title: t("Declined"),
					body: t("Event request is successfully declined"),
				});
				return true;
			}
			return false;
		},
		[editEntityId, queryClient, showToast, t],
	);

	const validate = useCallback(
		(values: DeclineFormValues) => {
			const errors: { [K in keyof DeclineFormValues]?: any } = {};

			if (!values.declinationReason) {
				errors.declinationReason = t("Field is required");
			}

			return errors;
		},
		[t],
	);

	return (
		<FormDrawer
			{...drawerProps}
			renderForm={(props) => (
				<FormDrawerContent {...props}>
					<TextareaField name="declinationReason" label={t("Declination reason")} required />
				</FormDrawerContent>
			)}
			initialValues={emptyDeclineFormValues}
			onSubmit={decline}
			title={t("Decline")}
			validate={validate}
		/>
	);
};
